import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ApproveButton from '../../../../components/Button/ApproveButton/ApproveButton';
import ResponsiveDialog from '../../../../components/Modal/modal';
import instance from '../../../../axios-default';
import { useDispatch, useSelector } from 'react-redux';
import { easySnackbar } from '../../../../store/Noti/noti.actions';
import PercentageProgressRing from '../../../../components/PercentageProgressRing';
import { setRefreshApiByUpdateRequest } from '../../../../store/SmileDesign/SmileDesignSlice';
import CustomModal, { localColorTheme } from '../../../../components/Modal/CustomModal';
import { Button, Grid, Radio, withStyles } from '@material-ui/core';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import RadioRhf from '../../../../components/ReactHookForm/RadioRhf/RadioRhf';
import { RadioOptionRhf } from '../../../../components/ReactHookForm/RadioRhf/RadioOptionRhf';
import CreateButton from '../../../../components/Button/CreateButton/CreateButton';
import frontend_config from '../../../../frontend_config';
import { useLocation, useHistory } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { handleTriggerRefetch } from '../../../ClinicalSimulation/RequestComment/RequestCommentSlice';
import { useLang } from '../../../../util/lang';

const PurpleRadio = withStyles({
    root: {
        color: localColorTheme.purple,
        '&$checked': {
            color: localColorTheme.purple,
        },
    },
    checked: {},
})((props) => <Radio color="default" {...props} />);

const ClinicalRequesMultipleModalButtons = ({ caseId, planId, disabled = false }) => {
    const methods = useForm({
        mode: 'onChange',
        resolver: yupResolver(
            yup.object().shape({
                upgradeTreatmentPlan: yup.string().required('Choose one option'),
            })
        ),
    });

    const [modelOpen, setModelOpen] = useState(false);
    const dispatch = useDispatch();
    const progressBarRef = useRef(null);

    // Usage: loading
    const [isLoading, setIsLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [data, setData] = useState({});

    // Usage: disable
    const [disableRequestComprehensive, setDisableRequestComprehensive] = useState(false);
    const [disableConfirmTwelveAligner, setDisableConfirmTwelveAligner] = useState(false);

    const location = useLocation();
    const history = useHistory();
    const pathname = location.pathname;
    const lastSegment = pathname.substring(pathname.lastIndexOf('/') + 1);

    const formValues = useWatch({ control: methods.control });

    const get = useLang('PatientInfo');

    const fetchSmileDesignField = async () => {
        setFetchLoading(true);
        try {
            const allowedUpgradeResponse = await instance.get(`/case/smileDesign/allowedUpgrade/${caseId}`);
            const { comprehensive, twelveStep } = allowedUpgradeResponse.data.result;
            setData(allowedUpgradeResponse.data.result);
            if (!comprehensive) {
                // if comprehensive is not allowed
                methods.setValue('upgradeTreatmentPlan', 'comprehensive');
                setDisableRequestComprehensive(true);
                setDisableConfirmTwelveAligner(true);
            } else {
                if (!twelveStep) {
                    // if twelveStep is not allowed
                    // methods.setValue('upgradeTreatmentPlan', 'twelveStep')
                    setDisableConfirmTwelveAligner(true);
                }
            }
            setFetchLoading(false);
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                dispatch(easySnackbar(err?.response?.data?.message, 'error'));
            }
        }
    };

    useEffect(() => {
        if (caseId) {
            fetchSmileDesignField();
        }
    }, [caseId]);

    useEffect(() => {
        if (modelOpen) {
            fetchSmileDesignField();
        }
    }, [modelOpen]);

    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            const response = await instance.post(`/case/smileDesign/requestUpgradeConfirm/${caseId}`, data, {
                onUploadProgress: progressBarRef.current?.onProgressChange,
            });
            if (response.data.success) {
                dispatch(setRefreshApiByUpdateRequest(true));
                dispatch(easySnackbar('Updated', 'success'));
                setModelOpen(false);
                dispatch(handleTriggerRefetch());

                if (lastSegment === 'treatment') {
                    // in view treatment page
                    // history.push(`/patients/${caseId}`);
                    window.location.reload();
                }
            }
        } catch (err) {
            if (err.response && err.response.data && err.response.data.message) {
                dispatch(easySnackbar(err?.response?.data?.message, 'error'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FormProvider {...methods}>
            <PercentageProgressRing ref={progressBarRef} open={isLoading} label={undefined} />
            <CreateButton
                name={'Request/Upgrade Treatment Plan'}
                onClick={() => {
                    setModelOpen(true);
                }}
                disabled={
                    disabled ||
                    Object.keys(data).length === 0 ||
                    (disableRequestComprehensive && disableConfirmTwelveAligner)
                }
                icon={false}
                buttonBackgroundColor={'#445994'}
                fixSize={false}
            />

            <CustomModal
                title={'Request / Upgrade TrioClear Treatment Plan'}
                open={modelOpen}
                handleClose={() => setModelOpen(false)}
                submitButton={true}
                handleDataSave={methods.handleSubmit(onSubmit)}
                submitButtonLabel={'CONFIRM'}
                closeButtonLabel={'BACK'}
                disabledSubmitButton={
                    (disableConfirmTwelveAligner && disableRequestComprehensive) ||
                    fetchLoading ||
                    formValues.upgradeTreatmentPlan === undefined
                }
                modalError={undefined}
            >
                <>
                    <Grid container style={{ paddingLeft: 16 }} spacing={3}>
                        <Grid item md={12} xs={12}>
                            <RadioRhf
                                key={'upgradeTreatmentPlan'}
                                question={''}
                                name={'upgradeTreatmentPlan'}
                                row={false}
                                formControlLabelRhf={
                                    <>
                                        <RadioOptionRhf
                                            value={'twelveStep'}
                                            radio={<PurpleRadio />}
                                            label={
                                                <p style={{ paddingLeft: 16 }}>
                                                    {get('clinicalSimulation.modal.requestTwelveStepOption')}
                                                </p>
                                            }
                                            disable={disableConfirmTwelveAligner}
                                            children={
                                                <p
                                                    style={{
                                                        paddingLeft: 30,
                                                        fontSize: 15,
                                                        color: disableConfirmTwelveAligner
                                                            ? localColorTheme.disabled
                                                            : undefined,
                                                    }}
                                                >
                                                    {get('clinicalSimulation.modal.requestTwelveStepInfo')}
                                                </p>
                                            }
                                        />

                                        <RadioOptionRhf
                                            value={'comprehensive'}
                                            radio={<PurpleRadio />}
                                            label={
                                                <p style={{ paddingLeft: 16 }}>
                                                    {get('clinicalSimulation.modal.requestComprehensiveOption')}
                                                </p>
                                            }
                                            disable={disableRequestComprehensive}
                                            children={
                                                <>
                                                    <p
                                                        style={{
                                                            paddingLeft: 25,
                                                            fontSize: 15,
                                                            color: disableRequestComprehensive
                                                                ? localColorTheme.disabled
                                                                : undefined,
                                                        }}
                                                    >
                                                        {get('clinicalSimulation.modal.requestComprehensiveInfo')}
                                                    </p>
                                                    <p
                                                        style={{
                                                            paddingLeft: 25,
                                                            paddingTop: 20,
                                                            fontSize: 15,
                                                            color: disableRequestComprehensive
                                                                ? localColorTheme.disabled
                                                                : undefined,
                                                        }}
                                                    >
                                                        {get('clinicalSimulation.modal.requestComprehensiveInfo2')}
                                                    </p>
                                                </>
                                            }
                                        />
                                    </>
                                }
                            />
                        </Grid>
                    </Grid>
                </>
            </CustomModal>
        </FormProvider>
    );
};

export default memo(ClinicalRequesMultipleModalButtons);
